import Url from "@core-ui/ApiServices/Types/Url";
import { type OnLinkOpen, searchLinkTarget } from "@ext/serach/components/hooks/useSearchResults";
import { SearchFocusableWrapper } from "@ext/serach/components/results/SearchFocusableWrapper";
import { SearchResultArticleItem } from "@ext/serach/components/results/SearchResultArticleItem";
import { SearchResultDiagramHeaderItem } from "@ext/serach/components/results/SearchResultDiagramHeaderItem";
import { SearchResultExpanderItem } from "@ext/serach/components/results/SearchResultExpanderItem";
import { SearchResultFileHeaderItem } from "@ext/serach/components/results/SearchResultFileHeaderItem";
import { SearchResultHeaderItem } from "@ext/serach/components/results/SearchResultHeaderItem";
import { SearchResultNest } from "@ext/serach/components/results/SearchResultNest";
import { SearchResultParagraphItem } from "@ext/serach/components/results/SearchResultParagraphItem";
import type { ArticleRow, ArticleRowItem } from "@ext/serach/components/rowTypes";
import type { FocusItem } from "@ext/serach/utils/FocusItemsCollector";
import type { SearchItemRowId } from "@ext/serach/utils/SearchRowsModel";
import { Fragment } from "react";

export interface SearchResultArticleBlockProps {
	row: ArticleRow;
	isCurrent?: boolean;
	showCatalog: boolean;
	onLinkOver: (id: SearchItemRowId) => void;
	onFocus: (id: SearchItemRowId) => void;
	onLinkOpen: OnLinkOpen;
	focusItem: FocusItem | undefined;
	focusRef: React.RefObject<HTMLElement>;
}

export const SearchResultArticleBlock = (props: SearchResultArticleBlockProps) => {
	const { row, isCurrent, showCatalog, onFocus, onLinkOpen, onLinkOver, focusItem, focusRef } = props;

	return (
		<div>
			<SearchFocusableWrapper
				focusItem={focusItem}
				focusRef={focusRef}
				item={{
					id: row.id,
					focusable: true,
				}}
				onClick={() => onLinkOpen(searchLinkTarget(row))}
				onFocus={onFocus}
				onLinkOver={onLinkOver}
			>
				<SearchResultArticleItem
					breadcrumbs={row.rawResult.breadcrumbs}
					catalog={{
						name: row.rawResult.catalog.name,
						title: row.rawResult.catalog.title,
						url: row.rawResult.catalog.url,
					}}
					isCurrent={isCurrent}
					isRecommended={row.rawResult.isRecommended}
					onBreadcrumbOpen={(pathname) => onLinkOpen({ url: Url.from({ pathname }), pathname })}
					onOpen={() => onLinkOpen(searchLinkTarget(row))}
					showCatalog={showCatalog}
					title={row.rawResult.title}
					url={row.url}
				/>
			</SearchFocusableWrapper>
			{renderChildren(row.items, props)}
		</div>
	);
};

interface RenderContext {
	onLinkOver: (id: SearchItemRowId) => void;
	onFocus: (id: SearchItemRowId) => void;
	onLinkOpen: OnLinkOpen;
	focusItem: FocusItem | undefined;
	focusRef: React.RefObject<HTMLElement>;
}

const focusProps = (item: ArticleRowItem, ctx: RenderContext) => ({
	focusItem: ctx.focusItem,
	focusRef: ctx.focusRef,
	item,
	onFocus: ctx.onFocus,
	onLinkOver: ctx.onLinkOver,
});

const renderItem = (item: ArticleRowItem, ctx: RenderContext) => {
	const type = item.type;
	switch (type) {
		case "link": {
			return (
				<SearchFocusableWrapper
					{...focusProps(item, ctx)}
					onClick={() => ctx.onLinkOpen(searchLinkTarget(item))}
					url={item.url}
				>
					<SearchResultParagraphItem text={item.marks} />
				</SearchFocusableWrapper>
			);
		}
		case "block": {
			return (
				<>
					<SearchFocusableWrapper
						{...focusProps(item, ctx)}
						onClick={() => ctx.onLinkOpen(searchLinkTarget(item))}
						url={item.url}
					>
						<SearchResultHeaderItem breadcrumbs={item.breadcrumbs} />
					</SearchFocusableWrapper>
					{renderChildren(item.children, ctx)}
				</>
			);
		}
		case "file-block": {
			return (
				<SearchFocusableWrapper
					{...focusProps(item, ctx)}
					onClick={() => ctx.onLinkOpen(searchLinkTarget(item))}
					url={item.url}
				>
					<SearchResultFileHeaderItem fileName={item.fileName} title={item.title} />
					{renderChildren(item.children, ctx)}
				</SearchFocusableWrapper>
			);
		}
		case "diagram": {
			return (
				<SearchFocusableWrapper
					{...focusProps(item, ctx)}
					onClick={() => ctx.onLinkOpen(searchLinkTarget(item))}
					url={item.url}
				>
					<SearchResultDiagramHeaderItem title={item.title} />
					{renderChildren(item.children, ctx)}
				</SearchFocusableWrapper>
			);
		}
		case "expander": {
			return (
				<SearchFocusableWrapper {...focusProps(item, ctx)} onClick={() => item.onClick()}>
					<SearchResultExpanderItem count={item.count} />
				</SearchFocusableWrapper>
			);
		}
		default:
			throw new Error(`Unexpected row item type ${type}`);
	}
};

const renderChildren = (items: ArticleRowItem[], ctx: RenderContext) => {
	return (
		<SearchResultNest>
			{items.map((x) => (
				<Fragment key={x.id}>{renderItem(x, ctx)}</Fragment>
			))}
		</SearchResultNest>
	);
};
